/* eslint-disable react/no-unescaped-entities */
/**
 * ActionFocusedDashboard.jsx
 * v3.14-108: UI Simplification - Action-Focused Dashboard
 * 
 * Core Principle: Every screen answers instantly:
 * - What is this?
 * - What do I do next?
 * - What happens if I don't?
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import {
  AlertCircle,
  Clock,
  Phone,
  ChevronRight,
  RefreshCw,
  User,
  Calendar,
  CheckCircle2,
  AlertTriangle,
  Loader2,
  X,
  Trash2,
} from 'lucide-react';
import { format, formatDistanceToNow, isToday, isTomorrow } from 'date-fns';
import { toast } from 'sonner';
import { PhoneClickable } from './PhoneQRModal';  // v3.14-109: Scan-to-Call QR

// v3.18-1306 — Client label resolver.  "Unknown" was previously shown
// when neither ``full_name`` nor ``primary_owner_name`` was populated,
// which felt unprofessional and gave operators nothing actionable.
// Fallback chain per V1 cleanup directive:
//   1. Client name           (full_name / primary_owner_name)
//   2. Application number    (application_number / app_number / deal_number / app_id)
//   3. Phone number          (phone / mobile_phone / primary_phone)
//   4. Email address         (email / primary_email)
//   5. "Pending Client Information"
const resolveClientLabel = (r) => {
  if (!r) return 'Pending Client Information';
  const name = (r.full_name || r.primary_owner_name || '').trim();
  if (name) return name;
  const appNo = (r.application_number || r.app_number || r.deal_number || r.app_id || '').toString().trim();
  if (appNo) return `App #${appNo}`;
  const phone = (r.phone || r.mobile_phone || r.primary_phone || '').toString().trim();
  if (phone) return phone;
  const email = (r.email || r.primary_email || '').toString().trim();
  if (email) return email;
  return 'Pending Client Information';
};

// Clear, action-oriented stage labels
const STAGE_ACTIONS = {
  lead: { label: 'New Lead', action: 'Contact client', urgent: true },
  application: { label: 'Application', action: 'Review & submit', urgent: true },
  approved: { label: 'Approved', action: 'Send contract', urgent: true },
  contract_sent: { label: 'Contract Sent', action: 'Follow up', urgent: false },
  contract_signed: { label: 'Contract Signed', action: 'Send invoice', urgent: true },
  invoice_sent: { label: 'Invoice Sent', action: 'Confirm payment', urgent: false },
  payment_processing: { label: 'Processing', action: 'Verify ACH', urgent: true },
  funded: { label: 'Funded', action: 'Complete deal', urgent: false },
  completed: { label: 'Completed', action: 'Done', urgent: false },
};

// Priority colors
const PRIORITY_STYLES = {
  urgent: 'border-l-4 border-l-red-500 bg-red-50',
  action: 'border-l-4 border-l-amber-500 bg-amber-50',
  waiting: 'border-l-4 border-l-blue-500 bg-blue-50',
  followup: 'border-l-4 border-l-purple-500 bg-purple-50',
};

const ActionFocusedDashboard = () => {
  const { user, isPtcUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Simplified data structure
  const [needsAction, setNeedsAction] = useState([]);
  const [waitingDeals, setWaitingDeals] = useState([]);
  const [todaysFollowups, setTodaysFollowups] = useState([]);
  const [summary, setSummary] = useState({ action: 0, waiting: 0, followups: 0 });

  const fetchDashboardData = useCallback(async () => {
    try {
      // Fetch workflow data
      const [workflowRes, tasksRes] = await Promise.allSettled([
        api.get('/v2/dashboard/workflow'),
        api.get('/tasks?status=pending&limit=50'),
      ]);

      // Fetch dismissed items to filter them out
      let dismissedIds = new Set();
      try {
        const dismissedRes = await api.get('/dashboard/dismissed-actions');
        if (dismissedRes.data?.dismissed) {
          dismissedIds = new Set(dismissedRes.data.dismissed.map(d => d.item_id));
        }
      } catch (e) { /* ignore */ }

      // Process workflow data into action categories
      if (workflowRes.status === 'fulfilled') {
        const data = workflowRes.value.data;
        
        // NEEDS ACTION NOW - Urgent items requiring immediate attention
        const actionItems = [];
        
        // New applications that need claiming/processing
        if (data.new_applications?.length > 0) {
          data.new_applications.forEach(app => {
            actionItems.push({
              id: app.id,
              clientId: app.client_id,
              type: 'application',
              clientName: resolveClientLabel(app),
              phone: app.phone,
              email: app.email,
              stage: 'application',
              stageLabel: 'New Application',
              nextAction: 'Review & claim application',
              urgent: true,
              amount: app.amount_requested,
              createdAt: app.submission_time || app.created_at,
              waitingTime: app.submission_time ? formatDistanceToNow(new Date(app.submission_time), { addSuffix: false }) : null,
            });
          });
        }
        
        // In-review items that need action
        if (data.in_review?.length > 0) {
          data.in_review.forEach(app => {
            actionItems.push({
              id: app.id,
              clientId: app.client_id,
              type: 'review',
              clientName: resolveClientLabel(app),
              phone: app.phone,
              email: app.email,
              stage: 'in_review',
              stageLabel: 'In Review',
              nextAction: 'Complete review & submit',
              urgent: true,
              amount: app.amount_requested,
              createdAt: app.claimed_at || app.created_at,
            });
          });
        }
        
        // Active deals that need action based on stage
        if (data.active_deals?.length > 0) {
          data.active_deals.forEach(deal => {
            // v3.21-1640 — prefer the backend workflow resolver answer.
            // Falls back to the legacy STAGE_ACTIONS map only when the
            // deal row has no workflow_* fields (e.g. no deal_stages doc
            // exists yet for a brand-new lead).
            const wfLabel = deal.workflow_next_action_label;
            const wfCurrent = deal.workflow_current_stage_label;
            const wfClosed = deal.workflow_closed === true;
            const stageInfo = wfLabel || wfCurrent
              ? { label: wfCurrent || (STAGE_ACTIONS[deal.deal_stage]?.label || deal.deal_stage),
                  action: wfClosed ? 'Done' : (wfLabel || 'Follow up'),
                  urgent: !!wfLabel && !wfClosed }
              : (STAGE_ACTIONS[deal.deal_stage] || { label: deal.deal_stage, action: 'Review', urgent: false });
            
            if (stageInfo.urgent) {
              actionItems.push({
                id: deal.id,
                clientId: deal.client_id,
                type: 'deal',
                clientName: resolveClientLabel(deal),
                phone: deal.phone,
                email: deal.email,
                stage: deal.deal_stage,
                stageLabel: stageInfo.label,
                nextAction: stageInfo.action,
                urgent: true,
                amount: deal.deal_amount,
                createdAt: deal.updated_at || deal.created_at,
              });
            }
          });
        }
        
        const filteredActions = actionItems.filter(i => !dismissedIds.has(i.id));
        setNeedsAction(filteredActions.slice(0, 20)); // Limit to 20 items
        
        // WAITING ON CLIENT/FUNDING - Items that are pending external action
        const waitingItems = [];
        if (data.active_deals?.length > 0) {
          data.active_deals.forEach(deal => {
            // v3.21-1640 — same resolver-first pattern for the Waiting
            // bucket so the label matches the Clients table.
            const wfLabel = deal.workflow_next_action_label;
            const wfCurrent = deal.workflow_current_stage_label;
            const wfClosed = deal.workflow_closed === true;
            const stageInfo = wfLabel || wfCurrent
              ? { label: wfCurrent || (STAGE_ACTIONS[deal.deal_stage]?.label || deal.deal_stage),
                  action: wfClosed ? 'Done' : (wfLabel || 'Follow up'),
                  urgent: !!wfLabel && !wfClosed }
              : (STAGE_ACTIONS[deal.deal_stage] || { label: deal.deal_stage, action: 'Review', urgent: false });
            
            // Waiting stages: contract_sent, invoice_sent
            if (['contract_sent', 'invoice_sent'].includes(deal.deal_stage)) {
              waitingItems.push({
                id: deal.id,
                clientId: deal.client_id,
                type: 'deal',
                clientName: resolveClientLabel(deal),
                phone: deal.phone,
                email: deal.email,
                stage: deal.deal_stage,
                stageLabel: stageInfo.label,
                waitingFor: deal.deal_stage === 'contract_sent' ? 'Client signature' : 'Client payment',
                daysSince: deal.updated_at ? Math.floor((Date.now() - new Date(deal.updated_at).getTime()) / (1000 * 60 * 60 * 24)) : 0,
                amount: deal.deal_amount,
              });
            }
          });
        }
        
        setWaitingDeals(waitingItems);
        
        // Update summary
        setSummary(prev => ({
          ...prev,
          action: actionItems.length,
          waiting: waitingItems.length,
        }));
      }
      
      // TODAY'S FOLLOW-UPS from tasks
      if (tasksRes.status === 'fulfilled') {
        const tasks = tasksRes.value.data || [];
        const todaysTasks = tasks.filter(task => {
          if (!task.due_date) return false;
          const dueDate = new Date(task.due_date);
          return isToday(dueDate) || isTomorrow(dueDate);
        }).map(task => ({
          id: task.id,
          clientId: task.client_id,
          clientName: task.client_name || 'Unknown',
          taskTitle: task.title,
          dueDate: task.due_date,
          dueTime: task.due_time,
          isOverdue: new Date(task.due_date) < new Date(),
          phone: task.client_phone,
        }));
        
        setTodaysFollowups(todaysTasks);
        setSummary(prev => ({
          ...prev,
          followups: todaysTasks.length,
        }));
      }
      
    } catch (error) {
      console.error('Dashboard fetch error:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
    // Refresh every 60 seconds
    const interval = setInterval(fetchDashboardData, 60000);
    return () => clearInterval(interval);
  }, [fetchDashboardData]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchDashboardData();
  };

  const goToClient = (clientId) => {
    if (!clientId) {
      toast.error('Client ID not available');
      return;
    }
    navigate(`/clients/${clientId}`);
  };

  // v3.15-933: Click-to-claim on a "New Application" action card. Stamps the
  // current user as `application_concierge` only — sales rep / ptc rep are
  // never modified. Surfaces 409 ("Already claimed by …") to the rep but
  // continues navigation so they can still view the client panel.
  const handleApplicationItemClick = async (item) => {
    if (!item?.clientId) {
      toast.error('Client ID not available');
      return;
    }
    if (item.type === 'application' && item.id) {
      try {
        const res = await api.post(`/v2/applications/${item.id}/assign`, {});
        toast.success(`Concierge Assigned: ${res.data?.application_concierge_name || 'you'}`);
      } catch (e) {
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail || 'Failed to claim';
        if (status === 409) {
          toast.warning(detail);
        } else if (status !== 403) {
          toast.error(detail);
        }
      }
    }
    navigate(`/clients/${item.clientId}`);
  };

  // v3.14-212: Dismiss individual item
  const [dismissing, setDismissing] = useState(null);

  const handleDismissItem = async (e, item, section) => {
    e.stopPropagation();
    setDismissing(item.id);
    try {
      await api.post('/dashboard/dismiss-action', { 
        item_id: item.id, 
        item_type: item.type,
        client_id: item.clientId 
      });
      if (section === 'action') {
        setNeedsAction(prev => prev.filter(i => i.id !== item.id));
        setSummary(prev => ({ ...prev, action: prev.action - 1 }));
      } else if (section === 'waiting') {
        setWaitingDeals(prev => prev.filter(i => i.id !== item.id));
        setSummary(prev => ({ ...prev, waiting: prev.waiting - 1 }));
      } else if (section === 'followup') {
        setTodaysFollowups(prev => prev.filter(i => i.id !== item.id));
        setSummary(prev => ({ ...prev, followups: prev.followups - 1 }));
      }
      toast.success('Item dismissed');
    } catch (err) {
      toast.error('Failed to dismiss');
    } finally {
      setDismissing(null);
    }
  };

  const handleClearAll = async (section) => {
    const items = section === 'action' ? needsAction : section === 'waiting' ? waitingDeals : todaysFollowups;
    if (!window.confirm(`Clear all ${items.length} items from this section?`)) return;
    try {
      await api.post('/dashboard/dismiss-action', { 
        clear_all: true, 
        section,
        item_ids: items.map(i => ({ id: i.id, type: i.type, client_id: i.clientId }))
      });
      if (section === 'action') { setNeedsAction([]); setSummary(prev => ({ ...prev, action: 0 })); }
      else if (section === 'waiting') { setWaitingDeals([]); setSummary(prev => ({ ...prev, waiting: 0 })); }
      else { setTodaysFollowups([]); setSummary(prev => ({ ...prev, followups: 0 })); }
      toast.success('All items cleared');
    } catch (err) {
      toast.error('Failed to clear');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="action-dashboard">
      {/* Header with summary counts */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Your Dashboard</h1>
          <p className="text-slate-500 mt-1">
            {summary.action > 0 
              ? `${summary.action} items need your attention`
              : 'All caught up!'}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleRefresh}
          disabled={refreshing}
          data-testid="refresh-dashboard"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Quick Stats Bar */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
          <div className="text-3xl font-bold text-red-700">{summary.action}</div>
          <div className="text-sm text-red-600 font-medium">Needs Action</div>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
          <div className="text-3xl font-bold text-blue-700">{summary.waiting}</div>
          <div className="text-sm text-blue-600 font-medium">Waiting</div>
        </div>
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-center">
          <div className="text-3xl font-bold text-purple-700">{summary.followups}</div>
          <div className="text-sm text-purple-600 font-medium">Follow-ups Today</div>
        </div>
      </div>

      {/* SECTION 1: NEEDS ACTION NOW */}
      <Card className="border-2 border-red-200" data-testid="needs-action-section">
        <CardHeader className="bg-red-50 border-b border-red-200">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-red-600" />
            <CardTitle className="text-red-900">Needs Action Now</CardTitle>
            <Badge variant="destructive" className="ml-auto">{needsAction.length}</Badge>
            {needsAction.length > 0 && (
              <Button
                size="sm"
                variant="ghost"
                className="h-7 text-xs text-red-600 hover:text-red-800 hover:bg-red-100"
                onClick={() => handleClearAll('action')}
                data-testid="clear-all-action"
              >
                <Trash2 className="h-3 w-3 mr-1" />
                Clear All
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {needsAction.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <CheckCircle2 className="h-12 w-12 mx-auto text-green-500 mb-2" />
              <p className="font-medium">All caught up!</p>
              <p className="text-sm">No urgent items requiring action</p>
            </div>
          ) : (
            <ScrollArea className="max-h-[400px]">
              <div className="divide-y divide-slate-100">
                {needsAction.map((item) => (
                  <div
                    key={`${item.type}-${item.id}`}
                    className={`p-4 hover:bg-slate-50 cursor-pointer transition-colors ${PRIORITY_STYLES.urgent}`}
                    onClick={() => handleApplicationItemClick(item)}
                    data-testid={`action-item-${item.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        {/* Client Name & Stage */}
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-blue-600 hover:underline truncate">
                            {item.clientName}
                          </span>
                          <Badge variant="outline" className="text-xs">
                            {item.stageLabel}
                          </Badge>
                        </div>
                        
                        {/* Next Action - THE MOST IMPORTANT INFO */}
                        <div className="flex items-center gap-2 text-red-700 font-medium">
                          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                          <span>{item.nextAction}</span>
                        </div>
                        
                        {/* Contact Info - v3.14-109: QR Modal */}
                        {item.phone && (
                          <div className="flex items-center gap-2 mt-2 text-sm text-slate-600">
                            <PhoneClickable 
                              phone={item.phone} 
                              name={item.clientName}
                              showIcon={true}
                            />
                          </div>
                        )}
                        
                        {/* Waiting time indicator */}
                        {item.waitingTime && (
                          <div className="text-xs text-red-600 mt-1">
                            Waiting: {item.waitingTime}
                          </div>
                        )}
                      </div>
                      
                      {/* Amount, Delete & Arrow */}
                      <div className="flex items-center gap-2 ml-4">
                        {item.amount > 0 && (
                          <span className="text-sm font-medium text-slate-700">
                            ${item.amount.toLocaleString()}
                          </span>
                        )}
                        <button
                          onClick={(e) => handleDismissItem(e, item, 'action')}
                          className="p-1 rounded hover:bg-red-100 text-slate-400 hover:text-red-600 transition-colors"
                          title="Dismiss"
                          data-testid={`dismiss-action-${item.id}`}
                        >
                          {dismissing === item.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}
                        </button>
                        <ChevronRight className="h-5 w-5 text-slate-400" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>

      {/* SECTION 2: WAITING ON CLIENT / FUNDING */}
      <Card className="border-2 border-blue-200" data-testid="waiting-section">
        <CardHeader className="bg-blue-50 border-b border-blue-200">
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-blue-600" />
            <CardTitle className="text-blue-900">Waiting on Client / Funding</CardTitle>
            <Badge className="ml-auto bg-blue-600">{waitingDeals.length}</Badge>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {waitingDeals.length === 0 ? (
            <div className="p-6 text-center text-slate-500">
              <p className="text-sm">No deals waiting for external action</p>
            </div>
          ) : (
            <ScrollArea className="max-h-[300px]">
              <div className="divide-y divide-slate-100">
                {waitingDeals.map((item) => (
                  <div
                    key={item.id}
                    className={`p-4 hover:bg-slate-50 cursor-pointer transition-colors ${PRIORITY_STYLES.waiting}`}
                    onClick={() => goToClient(item.clientId)}
                    data-testid={`waiting-item-${item.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-slate-900">{item.clientName}</span>
                          <Badge variant="outline" className="text-xs">{item.stageLabel}</Badge>
                        </div>
                        <div className="text-sm text-blue-700">
                          Waiting for: <span className="font-medium">{item.waitingFor}</span>
                        </div>
                        {item.daysSince > 3 && (
                          <div className="text-xs text-amber-600 mt-1">
                            ⚠️ {item.daysSince} days since last update
                          </div>
                        )}
                        {item.phone && (
                          <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                            <PhoneClickable 
                              phone={item.phone} 
                              name={item.clientName}
                              showIcon={true}
                            />
                          </div>
                        )}
                      </div>
                      <ChevronRight className="h-5 w-5 text-slate-400" />
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>

      {/* SECTION 3: TODAY'S FOLLOW-UPS */}
      <Card className="border-2 border-purple-200" data-testid="followups-section">
        <CardHeader className="bg-purple-50 border-b border-purple-200">
          <div className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-purple-600" />
            <CardTitle className="text-purple-900">Today's Follow-Ups</CardTitle>
            <Badge className="ml-auto bg-purple-600">{todaysFollowups.length}</Badge>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {todaysFollowups.length === 0 ? (
            <div className="p-6 text-center text-slate-500">
              <p className="text-sm">No follow-ups scheduled for today</p>
            </div>
          ) : (
            <ScrollArea className="max-h-[300px]">
              <div className="divide-y divide-slate-100">
                {todaysFollowups.map((item) => (
                  <div
                    key={item.id}
                    className={`p-4 hover:bg-slate-50 cursor-pointer transition-colors ${item.isOverdue ? PRIORITY_STYLES.urgent : PRIORITY_STYLES.followup}`}
                    onClick={() => item.clientId && goToClient(item.clientId)}
                    data-testid={`followup-item-${item.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-slate-900">{item.clientName}</div>
                        <div className="text-sm text-purple-700 mt-1">
                          📞 {item.taskTitle}
                        </div>
                        {item.dueTime && (
                          <div className="text-xs text-slate-500 mt-1">
                            Scheduled: {item.dueTime}
                          </div>
                        )}
                        {item.isOverdue && (
                          <div className="text-xs text-red-600 font-medium mt-1">
                            ⚠️ OVERDUE
                          </div>
                        )}
                      </div>
                      {item.phone && (
                        <PhoneClickable 
                          phone={item.phone} 
                          name={item.clientName}
                          className="ml-2 px-3 py-1.5 text-sm border rounded-md hover:bg-slate-50"
                          showIcon={true}
                        />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ActionFocusedDashboard;
